import React from 'react';
import get from 'lodash/get';
import { useDebounce } from 'react-use';
import { DataTable } from 'app/components/Charts/table';
import { useStoreActions, useStoreState } from 'app/state/store/hooks';
import {
  DonorsDataTableColumns,
  DonorsDataTableOptions,
} from 'app/components/Charts/table/data';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  selectedFilterAtom,
  transactionTypeAtom,
} from 'app/state/recoil/atoms';
import { getAPIFormattedFilters } from 'app/utils/getAPIFormattedFilters';
import { MUIDataTableOptions } from 'mui-datatables';

interface DonorsTableModuleProps {
  tableOptions?: MUIDataTableOptions;
}

export function DonorsTableModule(props: DonorsTableModuleProps) {
  const [selectedFilters] = useRecoilState(selectedFilterAtom);
  const [tablePage, setTablePage] = React.useState(0);
  const [tableRows, setTableRows] = React.useState(10);
  const [search, setSearch] = React.useState<undefined | string>();
  const transactionType = useRecoilValue(transactionTypeAtom);
  const [debouncedSearch, setDebouncedSearch] = React.useState<
    undefined | string
  >();
  const [tableSortBy, setTableSortBy] = React.useState<{
    name: string;
    direction: 'asc' | 'desc';
  }>({
    name: transactionType.name,
    direction: 'desc',
  });

  const action = useStoreActions((actions) => actions.donors.fetch);
  const actionData = useStoreState((actions) => actions.donors.data);

  const tableData = get(actionData, 'data', []);
  const tableCount = get(actionData, 'count', []);
  const tableOptions = props.tableOptions
    ? props.tableOptions
    : DonorsDataTableOptions;

  const searchTerm = useStoreState((state) => state.syncSearch.value);

  function callAction(page: number, rows: number, sort: string) {
    const filters = getAPIFormattedFilters(selectedFilters);
    action({
      values: {
        filters: searchTerm.length > 0 ? searchTerm : filters,
        page,
        rows,
        sort,
        search: debouncedSearch,
        transactionTypeCode: transactionType.code,
      },
    });
  }

  React.useEffect(() => {
    callAction(
      tablePage,
      tableRows,
      `${tableSortBy.name} ${tableSortBy.direction}`
    );
  }, [
    tablePage,
    tableRows,
    tableSortBy,
    selectedFilters,
    debouncedSearch,
    transactionType,
  ]);

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search]
  );

  return (
    <DataTable
      data={tableData}
      columns={DonorsDataTableColumns}
      options={{
        ...tableOptions,
        serverSide: true,
        sortOrder: tableSortBy,
        count: tableCount,
        onSearchChange: (searchText) =>
          searchText != null && setSearch(searchText),
        onSearchClose: () => {
          setSearch(undefined);
          setDebouncedSearch(undefined);
        },
        searchPlaceholder: 'Search donors',
        onChangePage: setTablePage,
        onChangeRowsPerPage: setTableRows,
        onColumnSortChange: (
          changedColumn: string,
          direction: 'asc' | 'desc'
        ) =>
          setTableSortBy({
            name: changedColumn,
            direction,
          }),
      }}
      title={`AIDA has found ${new Intl.NumberFormat('nl-NL').format(
        tableCount
      )} donors for you`}
    />
  );
}
